import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface DistributionData {
  x: number;
  y: number;
}

interface DistributionChartProps {
  data: DistributionData[];
  publisherValue: number;
  title: string;
  unit?: string;
  minValue: number;
  maxValue: number;
  optimalMin?: number;
  optimalMax?: number;
}

const DistributionChart: React.FC<DistributionChartProps> = ({
  data,
  publisherValue,
  title,
  unit = '',
  minValue,
  maxValue,
  optimalMin,
  optimalMax,
}) => {
  // Generate normal distribution curve data
  const generateDistributionData = () => {
    const labels = data.map(point => `${typeof point.x === 'number' ? point.x.toFixed(1) : point.x}${unit}`);
    const distributionValues = data.map(point => point.y);

    return {
      labels,
      datasets: [
        {
          label: 'Publisher Distribution',
          data: distributionValues,
          borderColor: 'rgb(99, 132, 255)',
          backgroundColor: 'rgba(99, 132, 255, 0.1)',
          fill: true,
          tension: 0.4,
          pointRadius: 0,
          borderWidth: 2,
        },
        // Optimal range overlay if provided
        ...(optimalMin !== undefined && optimalMax !== undefined ? [{
          label: 'Optimal Range',
          data: data.map(point => 
            point.x >= optimalMin && point.x <= optimalMax ? point.y : null
          ),
          borderColor: 'rgba(34, 197, 94, 0.8)',
          backgroundColor: 'rgba(34, 197, 94, 0.2)',
          fill: true,
          tension: 0.4,
          pointRadius: 0,
          borderWidth: 0,
        }] : []),
        // Publisher position line
        {
          label: 'Your Value',
          data: data.map(() => null), // Will be handled by annotation
          borderColor: 'rgb(239, 68, 68)',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          borderWidth: 3,
          pointRadius: 0,
        },
      ],
    };
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        callbacks: {
          title: (context: any) => `${context[0].label}`,
          label: (context: any) => {
            if (context.datasetIndex === 0) {
              return `${(context.parsed.y * 100).toFixed(1)}% of publishers`;
            }
            return '';
          },
        },
      },
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: title,
        },
        grid: {
          display: false,
        },
      },
      y: {
        display: true,
        title: {
          display: true,
          text: 'Publisher Distribution',
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          callback: function(value: any) {
            return `${(value * 100).toFixed(0)}%`;
          },
        },
      },
    },
    elements: {
      point: {
        radius: 0,
      },
    },
    interaction: {
      intersect: false,
    },
  };

  // Calculate percentile of publisher value
  const calculatePercentile = () => {
    const totalArea = data.reduce((sum, point, index) => {
      if (index === 0) return 0;
      const prevPoint = data[index - 1];
      const width = point.x - prevPoint.x;
      const avgHeight = (point.y + prevPoint.y) / 2;
      return sum + (width * avgHeight);
    }, 0);

    let areaBelow = 0;
    for (let i = 1; i < data.length; i++) {
      const point = data[i];
      const prevPoint = data[i - 1];
      
      if (point.x > publisherValue) {
        // Interpolate the exact position
        const ratio = (publisherValue - prevPoint.x) / (point.x - prevPoint.x);
        const interpolatedHeight = prevPoint.y + ratio * (point.y - prevPoint.y);
        const width = publisherValue - prevPoint.x;
        areaBelow += width * (prevPoint.y + interpolatedHeight) / 2;
        break;
      } else {
        const width = point.x - prevPoint.x;
        const avgHeight = (point.y + prevPoint.y) / 2;
        areaBelow += width * avgHeight;
      }
    }

    return Math.round((areaBelow / totalArea) * 100);
  };

  const percentile = calculatePercentile();

  return (
    <div className="distribution-chart-container">
      <div className="chart-wrapper" style={{ height: '120px', position: 'relative' }}>
        <Line data={generateDistributionData()} options={options} />
      </div>
      <div className="chart-stats">
        <div className="stat-item">
          <span className="stat-value">{typeof publisherValue === 'number' ? publisherValue.toFixed(1) : publisherValue}{unit}</span>
          <span className="stat-label">Your Value</span>
        </div>
        <div className="stat-item">
          <span className="stat-value">{percentile}%</span>
          <span className="stat-label">Percentile</span>
        </div>
        <div className="stat-item">
          <span className="stat-value">{minValue}{unit} - {maxValue}{unit}</span>
          <span className="stat-label">Range</span>
        </div>
      </div>
    </div>
  );
};

export default DistributionChart;